import React, {
  useCallback, useEffect, useState, useMemo,
} from 'react';
import { Grid } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import EditorJS from '@editorjs/editorjs';
import { useFormContext } from 'react-hook-form';
import uuid from 'uuid';

import { useFormikContext } from 'formik';
import { VideoInstruction, InstructionTypes } from '../../../../types/instructions';
import {
  setOpenVideoInstructionModal,
  getVideoListRequest,
} from '../../../../store/modules/script-editor/actions';
import { selectVideoInstruction } from '../../../../store/modules/script-editor/selectors';
import { selectProject } from '../../../../store/modules/projects/selectors';
import {
  InstructionEvent,
  InstructionActionTooltip,
} from '../types';
import { parseJsonToEditorStateWithInstruction } from '../../../../services/editorJS/utils';
import {
  emitInstructionEventByEditor, removeInstructionElement, setInstructionActionTooltip, sortDocumentsByTitle,
} from '../utils';
import {
  ModalComponent,
  InputComponent,
  SelectComponent,
  SmallImageItem, ProcessingMedia,
} from '../components';
import useStyles from './styles';
import { ListParams } from '../../../../types/main';
import { getQueryFilter } from '../../../../utility/helpers';
import { getVideoThumbnailUrl, getSmallVideoThumbnailUrl } from '../../../../views/projects/tab-media/media/utils';
import { ScreenLocations } from '../../../../configs/instructionConfig';
import { setMediaList, setOpenMediaViewer } from '../../../../store/modules/media-viewer/actions';
import { getStaticVideo } from '../../../../utility/mediaHelpers';
import DuplicateBlock from '../../../../services/editorJS/DuplicateBlock';
import { notify } from '../../../../store/modules/notifications/actions';

import { FormSimpleAutoComplete } from '../../..';
import { Option } from '../../../form-simple-autocomplete/FormSimpleAutoComplete';
import MediaFileMissingMessage from '../components/MediaFileMissingMessage';

type Props = {
  instanceEditor: EditorJS | undefined;
};

const scriptFieldName = 'script';

const defaultState = {
  comment: '',
  duration: '0.0',
  screenLocation: 0,
  startTime: '0.0',
  videoId: -1,
};

const VideoInstructionModal = ({ instanceEditor }: Props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { getValues, setValue } = useFormContext();
  const {
    isOpenModal, instructionId, isInline, videos,
  } = useSelector(selectVideoInstruction);
  const currentProject = useSelector(selectProject);
  const { setFieldValue } = useFormikContext();

  const [comment, setComment] = useState(defaultState.comment);
  const [duration, setDuration] = useState(defaultState.duration);
  const [screenLocation, setScreenLocation] = useState(defaultState.screenLocation);
  const [startTime, setStartTime] = useState(defaultState.startTime);
  const [videoId, setVideoId] = useState(defaultState.videoId);
  const [initialState, setInitialState] = useState(defaultState);

  const videoList = useMemo(() => {
    if (videos.data) {
      const sortedVideos = sortDocumentsByTitle(videos.data.items).map((item) => {
        const url = getSmallVideoThumbnailUrl(item.ref.video?.ref?.thumbnails || []);
        return {
          id: item.video_id,
          label: item.ref.video!.title,
          title: <SmallImageItem text={item.ref.video?.title || 'Video'} imageUrl={url} />,
        };
      });
      return [{ id: -1, label: 'None' }, ...sortedVideos];
    }
    return [];
  }, [videos.data]);

  const videoUrl = useMemo(() => {
    if (videos.data) {
      const article = videos.data.items.find((item) => item.video_id === videoId);
      if (article) {
        return getVideoThumbnailUrl(article.ref.video?.ref?.thumbnails || []);
      }
    }
    return '';
  }, [videos.data, videoId]);

  const showKebabMenu = useMemo(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
    return scriptData.instructions.some(
      (item) => item.id === instructionId,
    ) || scriptData.blocks.some((item) => item.data.instructionId === instructionId);
  }, [getValues, instructionId]);


  const closeHandler = useCallback(() => {
    if (instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.SET_VIDEO_INSTRUCTION_SELECTION,
        {},
      );
    }
    dispatch(setOpenVideoInstructionModal(false));
  }, [dispatch, instanceEditor]);

  const getSummary = useCallback(() => {
    if (videos.data) {
      const article = videos.data.items.find((item) => item.video_id === videoId);
      if (article) {
        return article.ref.video?.title || 'Video';
      }
    }
    if (comment.trim()) {
      return comment;
    }
    return 'Video';
  }, [comment, videos, videoId]);

  const saveHandler = useCallback(() => {
    if (instanceEditor) {
      (async () => {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instructionIndex = scriptData.instructions.findIndex(
          (item) => item.id === instructionId && item.type === InstructionTypes.videoInstruction,
        );
        const videoInstructions: VideoInstruction = {
          id: instructionId,
          type: InstructionTypes.videoInstruction,
          comment,
          needsInfo: false,
          data: {
            duration: Number(duration),
            videoId,
            startTime: Number(startTime),
            screenLocation,
          },
        };
        const summary = getSummary();
        if (instructionIndex !== -1) {
          scriptData.instructions[instructionIndex] = videoInstructions;
          if (isInline) {
            setInstructionActionTooltip(
              {
                instructionId,
                tooltip: `${InstructionActionTooltip.video}: ${summary}`,
                scriptBlocks: scriptData.blocks,
              },
            );
            emitInstructionEventByEditor(
              instanceEditor,
              InstructionEvent.UPDATE_VIDEO_ACTION_EVENT_LISTENERS,
              {
                instanceEditor,
                refEditor: window.scriptEditorRef!,
              },
            );
          }
          instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
        } else {
          const id = (instructionId.trim()) ? instructionId : uuid();
          videoInstructions.id = id;
          scriptData.instructions.push(videoInstructions);
          if (isInline) {
            emitInstructionEventByEditor(
              instanceEditor,
              InstructionEvent.SAVE_VIDEO_INSTRUCTION,
              { instructionId: id, tooltip: summary },
            );
          }
        }
        if (!isInline) {
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.SAVE_VIDEO_INSTRUCTION_BLOCK,
            { instructionId: videoInstructions.id, text: summary, tooltip: summary },
          );
        }
        setValue(scriptFieldName, JSON.stringify(scriptData));
        closeHandler();
      })();
    }
  }, [
    instanceEditor,
    getValues,
    setValue,
    videoId,
    duration,
    comment,
    screenLocation,
    startTime,
    instructionId,
    closeHandler,
    isInline,
    getSummary,
  ]);

  const cancelHandler = useCallback(() => {
    (async () => {
      setComment(initialState.comment);
      setScreenLocation(initialState.screenLocation);
      setStartTime(initialState.startTime);
      setVideoId(initialState.videoId);
      setDuration(initialState.duration);
      const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
      const isInstructionCreated = scriptData.instructions.some(
        (item) => item.id === instructionId && item.type === InstructionTypes.documentInstruction,
      ) || scriptData.blocks.some((item) => item.data.instructionId === instructionId);
      if (instanceEditor) {
        if (!isInstructionCreated) {
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.REMOVE_INSTRUCTION_BLOCK_WITHOUT_SAVING,
            {},
          );
        }
        emitInstructionEventByEditor(
          instanceEditor,
          InstructionEvent.CANCEL_INSTRUCTION,
          {},
        );
      }
      closeHandler();
    })();
  }, [
    setComment,
    setScreenLocation,
    setStartTime,
    setVideoId,
    setDuration,
    initialState,
    closeHandler,
    instanceEditor,
    instructionId,
    getValues,
  ]);

  const removeHandler = useCallback(() => {
    if (!isInline && instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.REMOVE_INSTRUCTION_BLOCK,
        {},
      );
    } else {
      removeInstructionElement({ instructionId, isInline })
        .then(() => {
          if (instanceEditor) {
            instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
          }
        });
    }
    closeHandler();
  }, [closeHandler, instructionId, isInline, instanceEditor]);

  const openPreview = useCallback(() => {
    if (videos.data) {
      const article = videos.data.items.find((item) => item.video_id === videoId);
      if (article) {
        const url = article.ref.video?.streaming_url
          || getStaticVideo(article.ref.video?.ref.statics || [])?.url || '';

        dispatch(setMediaList([{
          url,
          title: article.ref.video?.title || 'Video',
          id: article.video_id,
          typeId: 2,
        }]));
        dispatch(setOpenMediaViewer(true));
      }
    }
  }, [dispatch, videos, videoId]);

  const duplicate = useCallback(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues().script);
    const notifHandler = () => dispatch(
      notify({
        type: 'success',
        icon: 'copy',
        message: 'Instruction duplicated.',
      }),
    );
    if (instanceEditor) {
      const duplicateBlockService = new DuplicateBlock(instanceEditor, notifHandler, setValue, instructionId, scriptData);
      if (!isInline) {
        duplicateBlockService.duplicateBlock();
      } else {
        duplicateBlockService.duplicateInline();
      }
    }
  }, [instanceEditor, getValues, instructionId, isInline, setValue, dispatch]);

  const onVideoChangeHandler = useCallback((option?: Option) => {
    if (option) {
      setVideoId(parseInt(option.id.toString(), 10));
    }
  }, [setVideoId]);

  useEffect(() => {
    if (isOpenModal) {
      setFieldValue('video' as never, videoId);
    }
  }, [isOpenModal, videoId, setFieldValue]);

  useEffect(() => {
    if (!videos.loading) {
      (async () => {
        const { data } = currentProject;
        if (data) {
          const params: ListParams = {
            sort_dir: 'DESC',
            page_size: 50,
            filter: {
              project_id: data.id,
              is_media: 1,
              media_type_id: 2,
            },
          };
          if (videos.data === null) {
            dispatch(getVideoListRequest(params));
          } else {
            const projectId = getQueryFilter('project_id', videos.data.filters.active);
            if (projectId !== undefined && String(projectId.value) !== String(data.id)) {
              dispatch(getVideoListRequest(params));
            }
          }
        }
      })();
    }
  }, [dispatch, videos, currentProject]);

  useEffect(() => {
    if (instructionId) {
      (async () => {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instruction = scriptData.instructions.find(
          (item) => item.id === instructionId && item.type === InstructionTypes.videoInstruction,
        );

        if (instruction) {
          const { data } = instruction as VideoInstruction;
          setComment(instruction.comment);
          setScreenLocation(data.screenLocation);
          setStartTime(String(data.startTime));
          setVideoId(data.videoId);
          setInitialState({
            comment: instruction.comment,
            screenLocation: data.screenLocation,
            videoId: data.videoId,
            startTime: String(data.startTime),
            duration: String(data.duration),
          });
        } else {
          setComment(defaultState.comment);
          setScreenLocation(defaultState.screenLocation);
          setStartTime(defaultState.startTime);
          setVideoId(defaultState.videoId);
          setInitialState(defaultState);
        }
      })();
    } else {
      setComment(defaultState.comment);
      setScreenLocation(defaultState.screenLocation);
      setStartTime(defaultState.startTime);
      setVideoId(defaultState.videoId);
      setInitialState(defaultState);
    }
  }, [
    getValues,
    instructionId,
    setComment,
    setScreenLocation,
    setStartTime,
    setVideoId,
    setInitialState,
  ]);

  return (
    <ModalComponent
      open={isOpenModal}
      title={getSummary()}
      saveHandler={saveHandler}
      cancelHandler={cancelHandler}
      deleteHandler={removeHandler}
      duplicate={duplicate}
      showKebabMenu={showKebabMenu}
    >
      <Grid container spacing={3} justify="space-around">
        <Grid item xs={12}>
          <InputComponent
            type="textarea"
            label="Comment"
            value={comment}
            onChange={setComment}
            autoFocus
          />
        </Grid>
        <Grid item container spacing={3} xs={6} alignContent="flex-start">
          <Grid item xs={12} className={classes.selectVideoWrapper}>
            {/* <SelectComponent */}
            {/*  label="Video" */}
            {/*  value={videoId} */}
            {/*  items={videoList} */}
            {/*  changeHandler={setVideoId} */}
            {/* /> */}
            <FormSimpleAutoComplete
              name="video"
              label="Video"
              options={videoList}
              callback={onVideoChangeHandler}
              isClearValueIfOptionNull
            />
          </Grid>
          <Grid item xs={12}>
            <SelectComponent
              label="Screen Location"
              value={screenLocation}
              items={ScreenLocations}
              changeHandler={setScreenLocation}
              isNone={false}
            />
          </Grid>
          {/* <Grid item xs={12}> */}
          {/*  <InputComponent */}
          {/*    label="Start time" */}
          {/*    value={startTime} */}
          {/*    onChange={setStartTime} */}
          {/*  /> */}
          {/* </Grid> */}
          {/* - 2020.05,bn: disabled until we have clip start/stop selection
          <Grid item xs={12}>
            <InputComponent
              label="Duration"
              value={duration}
              onChange={setDuration}
            />
          </Grid>
          */}
        </Grid>
        <Grid item container spacing={3} xs={6} alignItems="flex-start" alignContent="flex-start">
          <p className={classes.videoTitle}>Preview</p>
          <Grid item container xs={12} alignItems="center" justify="center" className={classes.previewContainer}>
            {
              videoId < 0
                ? <MediaFileMissingMessage />
                : (
                  <>
                    <img onClick={openPreview} src={videoUrl} alt="" className={classes.video} />
                    {(!videoUrl && videoId !== -1) && <ProcessingMedia />}
                  </>
                )
            }
          </Grid>
        </Grid>
      </Grid>
    </ModalComponent>
  );
};

export default VideoInstructionModal;
